import * as React from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Box } from '@primer/react';
import { useAppContext } from '@/context/AppContext';
import { Head } from '../Head';
import LeftSidebar from './LeftSidebar';
import { TopBar } from './TopBar';
import { useTeamCreditsStore } from '@/stores/teamCredits';
import { useGetFlowscaleUser } from '@/features/users';
import ModalLogo from '@/assets/modal.svg';
import TawkToChat from '@/lib/TawkToChat';
import { ENVIRONMENT } from '@/config';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import { useGetProjectById, useGetPublicProjectbyId } from '@/features/projects';
import { useGetTeam } from '@/features/teams/api/getTeam';
import { useUserCredits } from '@/hooks/useUserCredits';
import storage from '@/utils/storage';

type MainLayoutProps = {
  children: React.ReactNode;
};

interface TeamMember {
  username: string;
  role: string;
  is_active: boolean;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const { data: user } = useGetFlowscaleUser();
  const userDetails = useCurrentUser();
  const { projectId } = useParams<{ projectId: any }>();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectInitiated = React.useRef(false);
  const hasRefetchedOnce = React.useRef(false);

  // React.useEffect(() => {
  //   if (storage.getOnboardingState() === 'incomplete' && storage.getOnboardingState()) {
  //     window.location.assign('/auth/onboarding');
  //   }
  // }, []);

  // React.useEffect(() => {
  //   if (storage.getMigrationStatus()) {
  //     window.location.assign('/auth/migration');
  //   }
  // }, []);

  // Get the username from either source
  const username = user?.username || userDetails?.username;

  // Check if we have both access token and username
  const hasUsername = !!username && username.length > 0;
  const shouldEnableQuery = !!projectId;

  const { data: publicRepoDetails, refetch } = useGetPublicProjectbyId(projectId, username, {
    enabled: shouldEnableQuery,
  });

  // Effect to handle refetch when access token is present and username is available
  React.useEffect(() => {
    const accessToken = storage.getAccessToken();
    const refreshToken = storage.getRefreshToken();

    // Only refetch once when we have access token, refresh token, username, and haven't refetched yet
    if (accessToken && refreshToken && hasUsername && !hasRefetchedOnce.current) {
      // console.log('Refetching public project data with access token and username');
      refetch();
      hasRefetchedOnce.current = true;
    }
  }, [refetch, hasUsername]); // Only depend on refetch and hasUsername

  // Reset the refetch flag when projectId changes (navigating to different project)
  React.useEffect(() => {
    hasRefetchedOnce.current = false;
  }, [projectId]);

  const {
    activeTabModule,
    setActiveTabModule,
    setIsEditorSidebarCollapsed,
    isEditorSidebarCollapsed,
    dummyProject,
    setDummyProject,
  } = useAppContext();
  const { credits } = useTeamCreditsStore();
  const { refreshConnection, userCredits: userCreditsValue } = useUserCredits(true);

  const showLogoWithFullName =
    location.pathname.includes('/app/projects') && !location.pathname.includes('/app/projects/');
  const hideOnProfilePage = location.pathname.includes('/app/profile');

  const { data: team, isLoading: isTeamLoading } = useGetTeam();

  const { data: privateRepoDetails } = useGetProjectById(projectId, {
    enabled: publicRepoDetails?.has_access ?? false,
  });

  const repoDetails = publicRepoDetails?.has_access ? privateRepoDetails : publicRepoDetails;

  const isUserTeamOwner = team?.data?.members?.some(
    (m: TeamMember) => m.username === user?.username && m.role === 'owner'
  );

  const isUserTeamMember = team?.data?.members.some(
    (m: TeamMember) => m?.username === user?.username && m.role === 'member'
  );

  // Set isViewOnlyMode cookie based on has_access
  React.useEffect(() => {
    // Don't run this effect if we're already on the not-authorized page
    if (location.pathname === '/not-authorized') {
      return;
    }

    if (publicRepoDetails) {
      // console.log('🔍 MainLayout Debug:', {
      //   visibility: publicRepoDetails?.visibility,
      //   can_view: publicRepoDetails?.can_view,
      //   has_access: publicRepoDetails?.has_access,
      //   projectData: publicRepoDetails,
      // });

      let isViewOnly = false;
      let shouldRedirect = false;

      if (publicRepoDetails?.visibility === 'organization') {
        // Handle organization visibility with can_view field
        if (publicRepoDetails?.can_view === false) {
          // console.log('❌ Redirecting: can_view is false');
          // Redirect to not-authorized if can_view is false
          shouldRedirect = true;
        } else if (publicRepoDetails?.can_view === true) {
          // console.log(
          //   '✅ Organization project with can_view=true, has_access=',
          //   publicRepoDetails?.has_access
          // );
          // If both has_access and can_view are true, set dummy to false
          // If only can_view is true (no has_access), set dummy to true
          isViewOnly = !publicRepoDetails?.has_access;
          shouldRedirect = false; // Explicitly set to false when can_view is true
        } else if (publicRepoDetails?.can_view === undefined && !publicRepoDetails?.has_access) {
          // console.log('❌ Redirecting: can_view is undefined and no has_access');
          // Fallback for organization without can_view field and no access
          shouldRedirect = true;
        }
      } else {
        // Existing logic for public and private projects
        isViewOnly =
          // Public projects without access
          (publicRepoDetails?.visibility === 'public' && !publicRepoDetails?.has_access) ||
          // Private projects without access
          (publicRepoDetails?.visibility === 'private' && !publicRepoDetails?.has_access);

        // Redirect to not-authorized for private repos without access
        shouldRedirect =
          publicRepoDetails?.visibility === 'private' && !publicRepoDetails?.has_access;
      }

      // console.log('🎯 Final decision:', { isViewOnly, shouldRedirect });

      setDummyProject(isViewOnly);

      // Handle redirection
      if (shouldRedirect && !redirectInitiated.current) {
        // console.log('🚨 Initiating redirect to /not-authorized');
        redirectInitiated.current = true;
        navigate('/not-authorized');
        return;
      }
    }
  }, [publicRepoDetails, setDummyProject, navigate, location.pathname]);

  // Reset redirect flag when leaving not-authorized page
  React.useEffect(() => {
    if (location.pathname !== '/not-authorized') {
      redirectInitiated.current = false;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (team) {
      refreshConnection();
    }
  }, [refreshConnection, team]);

  React.useEffect(() => {
    if (!storage.getAccessToken() && !storage.getRefreshToken()) {
      window.sessionStorage.setItem('redirect_project_id', JSON.stringify(projectId));
    }
  }, [projectId]);

  return (
    <Box className="h-screen flex flex-col overflow-hidden bg-[#16191D]">
      <Head
        title={
          activeTabModule === 'profile' && !showLogoWithFullName
            ? 'Profile'
            : activeTabModule !== 'profile'
            ? repoDetails?.name
            : null
        }
      />

      <TopBar
        userDetails={user}
        repoDetails={repoDetails}
        userCredits={userCreditsValue || credits}
        team={team}
        teamName={publicRepoDetails?.team_name}
        isUserTeamMember={isUserTeamMember}
        isUserTeamOwner={isUserTeamOwner}
        isTeamLoading={isTeamLoading}
      />

      {ENVIRONMENT !== 'development' && <TawkToChat />}
      <Box className="flex h-full overflow-hidden">
        {!showLogoWithFullName && !hideOnProfilePage && (
          <LeftSidebar
            activeTab={activeTabModule}
            setActiveTab={setActiveTabModule}
            userDetails={user}
            isEditorSidebarCollapsed={isEditorSidebarCollapsed}
            setIsEditorSidebarCollapsed={setIsEditorSidebarCollapsed}
            dummyProject={dummyProject}
          />
        )}

        <main className="flex-1 relative flex-grow h-full focus:outline-none">{children}</main>
      </Box>
      {showLogoWithFullName ? (
        <Box className="flex justify-end border-t border-[#22262C] px-[120px] pb-1.5 pt-1">
          <span
            onClick={() => {
              window.open('https://modal.com/', '_blank');
            }}
            className="text-sm text-gray-500 flex items-center gap-2 cursor-pointer"
          >
            Powered by <img src={ModalLogo} className="h-3" alt="modal-logo" />
          </span>
        </Box>
      ) : null}
    </Box>
  );
};
